import React, { useState, useMemo, useCallback } from "react";
import { useSelector, shallowEqual } from "react-redux";
import { useQuery, useMutation } from "@apollo/client";
import { toastr } from "react-redux-toastr";
import moment from "moment";
import { Alert } from "react-bootstrap";
import {
  Card,
  CardBody,
  CardHeader,
} from "../../../_metronic/_partials/controls";
import TableCardHeader from "../Table/TableCardHeader";
import AgGridTable from "../Table/AgGridTable";
import ReportDelegationModal from "./ReportDelegationModal";
import { GET_DELEGATIONS } from "./_Commons/Constants/Queries/Delegation";
import {
  CREATE_DELEGATION,
  DELETE_DELEGATION,
  UPDATE_DELEGATION,
} from "./_Commons/Constants/Mutations/Delegation";
import {
  GET_CG_USER_ROLE_ASSIGMENTS,
  GET_LG_USER_ROLE_ASSIGMENTS,
} from "./_Commons/Constants/Queries/Delegation";

// ==================== USER TYPES CONSTANTS ====================
const USER_TYPES = {
  SuperAdmin: 1,
  CG: 2,
  SEPC: 3,
  LG: 4,
};

// ==================== ROLE IDS CONSTANTS ====================
const ROLES = {
  LG: {
    ADMIN: 4,
    BPED: 7,
    BPED_MASTER: 53,
    ACCOUNTING_OFFICER: 6,
  },
  CG: {
    ADMIN: 2,
    BPED: 12,
    BPED_MASTER: 52,
    ACCOUNTING_OFFICER: 9,
    SEPC_ACCOUNTING_OFFICER: 28,
  },
};

const formatDate = (date) => (date ? moment(date).format("DD/MM/YYYY") : "N/A");

function ReportDelegation() {
  const { user, userRole, userVote, activeFiscalYear } = useSelector(
    (state) => state.auth,
    shallowEqual,
  );

  // ==================== DETECT USER TYPE FROM REDUX ====================
  const userTypeId = useMemo(() => {
    if (!user) return null;

    // Check if User_Type_ID is an object (populated)
    if (user.User_Type_ID && typeof user.User_Type_ID === "object") {
      return user.User_Type_ID.User_Type_ID;
    }

    // Check if User_Type_ID is a primitive value
    if (user.User_Type_ID) {
      return user.User_Type_ID;
    }

    // Check User_Type field (string)
    if (user.User_Type) {
      const userTypeStr = user.User_Type.toUpperCase();
      if (userTypeStr.includes("SUPER") || userTypeStr.includes("ADMIN")) {
        return USER_TYPES.SuperAdmin;
      } else if (userTypeStr.includes("LG") || userTypeStr.includes("LOCAL")) {
        return USER_TYPES.LG;
      } else if (userTypeStr.includes("SEPC")) {
        return USER_TYPES.SEPC;
      } else if (userTypeStr.includes("CG") || userTypeStr.includes("CENTRAL")) {
        return USER_TYPES.CG;
      }
    }

    return USER_TYPES.CG; // Default to CG
  }, [user]);

  const isSuperAdmin = userTypeId === USER_TYPES.SuperAdmin;
  const isLG = userTypeId === USER_TYPES.LG;
  const isCG = userTypeId === USER_TYPES.CG;
  const isSEPC = userTypeId === USER_TYPES.SEPC;
  const detectedUserType = isSuperAdmin
    ? "SuperAdmin"
    : isLG
      ? "LG"
      : isCG
        ? "CG"
        : isSEPC
          ? "SEPC"
          : "CG";

  // ==================== GET ROLE ID FROM REDUX ====================
  const roleId = useMemo(() => {
    if (!userRole) return null;

    // Check if userRole is an object with Role_ID
    if (typeof userRole === "object") {
      return userRole?.Role_ID || userRole?.ID;
    }

    // If userRole is a primitive value (ID)
    return userRole;
  }, [userRole]);

  // ==================== GET ROLE TITLE FROM REDUX ====================
  const roleTitle = useMemo(() => {
    if (!userRole) return "";

    if (typeof userRole === "object") {
      return userRole?.Title || "";
    }

    // Try to get from user object
    if (user?.Role_ID && typeof user.Role_ID === "object") {
      return user.Role_ID.Title || "";
    }

    return "";
  }, [userRole, user]);

  // ==================== DETECT ROLES ====================
  const roles = isLG ? ROLES.LG : ROLES.CG;

  const isBPEDMaster = useMemo(() => {
    if (!roleId || isSuperAdmin) return false;

    // Check by ID
    if (roleId === roles.BPED_MASTER) return true;

    // Check by title
    if (roleTitle && roleTitle.toLowerCase().includes("bped master")) return true;

    return false;
  }, [roleId, roleTitle, roles, isSuperAdmin]);

  const isAdmin = useMemo(() => {
    if (!roleId || isSuperAdmin) return false;

    // Check by ID
    if (roleId === roles.ADMIN) return true;

    // Check by title
    if (roleTitle && roleTitle.toLowerCase().includes("administrator")) return true;

    return false;
  }, [roleId, roleTitle, roles, isSuperAdmin]);

  const isAccountingOfficer = useMemo(() => {
    if (!roleId || isSuperAdmin) return false;

    // Check by ID
    if (isLG && roleId === ROLES.LG.ACCOUNTING_OFFICER) return true;
    if (isCG && roleId === ROLES.CG.ACCOUNTING_OFFICER) return true;
    if (isSEPC && roleId === ROLES.CG.SEPC_ACCOUNTING_OFFICER) return true;

    // Check by title
    if (roleTitle && roleTitle.toLowerCase().includes("accounting officer")) {
      return true;
    }

    return false;
  }, [roleId, roleTitle, isLG, isCG, isSEPC, isSuperAdmin]);

  const isHighLevelUser = isAdmin || isBPEDMaster;

  // ==================== GET VOTE DATA FROM REDUX ====================
  const voteId = useMemo(() => {
    if (!userVote) return 0;

    if (typeof userVote === "object") {
      return userVote?.Vote_ID || 0;
    }

    return userVote || 0;
  }, [userVote]);

  const voteName = useMemo(() => {
    if (!userVote) return "";

    if (typeof userVote === "object") {
      return userVote?.Vote_Name || "";
    }

    return "";
  }, [userVote]);

  const voteCode = useMemo(() => {
    if (!userVote) return "";

    if (typeof userVote === "object") {
      return userVote?.Vote_Code || "";
    }

    return "";
  }, [userVote]);

  const voteDisplayInfo = useMemo(() => {
    if (!userVote) return "Not assigned";
    if (voteCode && voteName) {
      return `${voteCode} - ${voteName}`;
    }
    if (voteName) return voteName;
    return "Not assigned";
  }, [userVote, voteCode, voteName]);

  const userName = useMemo(() => {
    if (!user) return "";
    return user.User_Name || user.UserName || "";
  }, [user]);

  // ==================== QUERY FOR USER ASSIGNMENTS ====================
  const { data: userAssignmentsData, loading: assignmentsLoading } = useQuery(
    isLG ? GET_LG_USER_ROLE_ASSIGMENTS : GET_CG_USER_ROLE_ASSIGMENTS,
    {
      skip: !user?.User_ID,
      fetchPolicy: "cache-first",
      onError: (error) => {
        console.error(`${detectedUserType} user roles error:`, error);
        toastr.error('Error', `Failed to load user assignments: ${error.message}`);
      },
    }
  );

  // ==================== SUPERADMIN NEEDS BOTH SIDES ====================
  const { data: saLGAssignmentsData } = useQuery(GET_LG_USER_ROLE_ASSIGMENTS, {
    skip: !isSuperAdmin,
    fetchPolicy: "cache-first",
  });

  const { data: saCGAssignmentsData } = useQuery(GET_CG_USER_ROLE_ASSIGMENTS, {
    skip: !isSuperAdmin,
    fetchPolicy: "cache-first",
  });

  // ==================== PROCESS ACCOUNTING OFFICERS ====================
  const {
    accountingOfficerOptions,
    altAccountingOfficerOptions,
    allAccountingOfficersMap,
  } = useMemo(() => {
    const accountingOfficers = [];
    const altAccountingOfficers = [];
    const accountingOfficersMap = new Map();

    const getAssignmentRoleId = (assignment) => {
      return (
        assignment.Role_ID?.Role_ID ||
        assignment.Role_ID?.ID ||
        assignment.Role_ID
      );
    };

    const getAssignmentVoteId = (assignment) => {
      return assignment.Vote_ID?.Vote_ID || assignment.Vote_ID;
    };

    const isAccountingOfficerRole = (assignment) => {
      const assignmentRoleId = getAssignmentRoleId(assignment);
      const assignmentRoleTitle = assignment.Role_ID?.Title || "";

      if (isLG) {
        return (
          assignmentRoleId === ROLES.LG.ACCOUNTING_OFFICER ||
          assignmentRoleTitle.toLowerCase().includes("accounting officer")
        );
      } else if (isSEPC) {
        return (
          assignmentRoleId === ROLES.CG.SEPC_ACCOUNTING_OFFICER ||
          assignmentRoleTitle.toLowerCase().includes("sepc accounting officer")
        );
      } else {
        return (
          assignmentRoleId === ROLES.CG.ACCOUNTING_OFFICER ||
          (assignmentRoleTitle.toLowerCase().includes("accounting officer") &&
            !assignmentRoleTitle.toLowerCase().includes("sepc"))
        );
      }
    };

    const processAssignments = (assignments, filterByVote = true) => {
      if (!assignments?.length) return;

      for (let i = 0; i < assignments.length; i++) {
        const assignment = assignments[i];

        if (assignment.Status !== "Active") continue;

        // Filter by vote if required
        if (filterByVote && voteId) {
          const assignmentVoteId = getAssignmentVoteId(assignment);
          if (assignmentVoteId !== voteId) continue;
        }

        if (!isAccountingOfficerRole(assignment)) continue;

        const userId = assignment.User_ID?.User_ID || assignment.User_ID;
        const firstName = assignment.User_ID?.First_Name || "";
        const lastName = assignment.User_ID?.Last_Name || "";
        const assignmentUserName =
          assignment.User_ID?.User_Name || assignment.User_Name || "";

        let displayName = "";
        if (firstName && lastName) {
          displayName = `${firstName} ${lastName}`;
          if (assignmentUserName) displayName += ` (${assignmentUserName})`;
        } else {
          displayName = assignmentUserName || `User ${userId}`;
        }

        const isCurrentUser = userId === user?.User_ID;

        // Add to map for name lookup
        if (!accountingOfficersMap.has(userId)) {
          accountingOfficersMap.set(userId, {
            value: userId,
            label: displayName,
            userId,
            userName: assignmentUserName,
            firstName,
            lastName,
            roleTitle: assignment.Role_ID?.Title || "",
          });
        }

        // Only add to options if from current vote (for selection)
        if (!filterByVote || getAssignmentVoteId(assignment) === voteId) {
          if (!accountingOfficers.some((ao) => ao.value === userId)) {
            accountingOfficers.push({
              value: userId,
              label: `${displayName}${isCurrentUser ? " (You)" : ""}`,
              isCurrentUser: isCurrentUser,
            });

            if (!isCurrentUser) {
              altAccountingOfficers.push({
                value: userId,
                label: displayName,
              });
            }
          }
        }
      }
    };

    // Process current user's assignments
    if (userAssignmentsData) {
      const assignments = isLG
        ? userAssignmentsData.lgUserRoleAssignments
        : userAssignmentsData.cgUserRoleAssignments;
      processAssignments(assignments, true);
    }

    // SuperAdmin can see both LG and CG assignments
    if (isSuperAdmin) {
      if (saLGAssignmentsData?.lgUserRoleAssignments) {
        processAssignments(saLGAssignmentsData.lgUserRoleAssignments, false);
      }
      if (saCGAssignmentsData?.cgUserRoleAssignments) {
        processAssignments(saCGAssignmentsData.cgUserRoleAssignments, false);
      }
    }

    accountingOfficers.sort((a, b) => {
      if (a.isCurrentUser) return -1;
      if (b.isCurrentUser) return 1;
      return a.label.localeCompare(b.label);
    });

    altAccountingOfficers.sort((a, b) => a.label.localeCompare(b.label));

    return {
      accountingOfficerOptions: accountingOfficers,
      altAccountingOfficerOptions: altAccountingOfficers,
      allAccountingOfficersMap: accountingOfficersMap,
    };
  }, [
    userAssignmentsData,
    saLGAssignmentsData,
    saCGAssignmentsData,
    voteId,
    isLG,
    isCG,
    isSEPC,
    isSuperAdmin,
    user?.User_ID,
  ]);

  // ==================== STATUS OPTIONS ====================
  const statusOptions = useMemo(
    () => [
      { value: "Active", label: "Active" },
      { value: "Approved", label: "Approved" },
      { value: "Rejected", label: "Rejected" },
    ],
    [],
  );

  const { data, loading, refetch } = useQuery(GET_DELEGATIONS, {
    fetchPolicy: "cache-first",
  });

  const [modalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState("Add");
  const [selectedRow, setSelectedRow] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [createDelegation, { loading: createLoading }] = useMutation(CREATE_DELEGATION, {
    onCompleted: () => {
      toastr.success("Success", "Delegation created successfully");
      refetch();
      setModalOpen(false);
      setIsSubmitting(false);
    },
    onError: (error) => {
      toastr.error("Error", error.message || "Failed to create delegation");
      setIsSubmitting(false);
    },
  });

  const [updateDelegation, { loading: updateLoading }] = useMutation(UPDATE_DELEGATION, {
    onCompleted: () => {
      toastr.success("Success", "Delegation updated successfully");
      refetch();
      setModalOpen(false);
      setIsSubmitting(false);
    },
    onError: (error) => {
      toastr.error("Error", error.message || "Failed to update delegation");
      setIsSubmitting(false);
    },
  });

  const [deleteDelegation] = useMutation(DELETE_DELEGATION, {
    onCompleted: () => {
      toastr.success("Success", "Delegation deleted successfully");
      refetch();
    },
    onError: (error) => {
      toastr.error("Error", error.message || "Failed to delete delegation");
    },
  });

  // ==================== FILTER DELEGATIONS BASED ON USER TYPE AND ROLE ====================
  const filteredDelegations = useMemo(() => {
    if (!data?.Delegations) return [];
    let filtered = data.Delegations;

    if (isSuperAdmin) {
      // Super Admin can see ALL delegations
      return filtered;
    }

    if (isBPEDMaster) {
      // BPED Master can see all delegations of their type
      filtered = filtered.filter((d) => {
        const lgVoteId = Number(d.LG_Vote_ID);
        const cgVoteId =
          typeof d.Vote_ID === "object"
            ? Number(d.Vote_ID?.Vote_ID)
            : Number(d.Vote_ID);

        if (isLG) {
          return lgVoteId > 0;
        } else if (isSEPC) {
          // SEPC uses Vote_ID (CG side)
          return cgVoteId > 0;
        } else {
          return cgVoteId > 0;
        }
      });
    } else if (isAccountingOfficer) {
      // ACCOUNTING OFFICER: Can only see delegations from their own vote or involving them
      filtered = filtered.filter((d) => {
        const delegationVoteId = isLG ? d.LG_Vote_ID : d.Vote_ID;
        const delegationVoteIdNum =
          typeof delegationVoteId === "object"
            ? delegationVoteId?.Vote_ID
            : delegationVoteId;
        const belongsToUserVote = delegationVoteIdNum === voteId;
        const isInvolved =
          d.User_ID === user?.User_ID || d.Alternate_User_ID === user?.User_ID;

        return belongsToUserVote || isInvolved;
      });
    } else {
      // OTHER USERS: Only show delegations where they are directly involved
      filtered = filtered.filter(
        (d) =>
          d.User_ID === user?.User_ID || d.Alternate_User_ID === user?.User_ID,
      );
    }
    return filtered;
  }, [
    data,
    isLG,
    isSEPC,
    voteId,
    user?.User_ID,
    isBPEDMaster,
    isAccountingOfficer,
    isSuperAdmin,
  ]);

  // ==================== ROW DATA FOR GRID ====================
  const rowData = useMemo(() => {
    if (!filteredDelegations.length) return [];

    return filteredDelegations.map((d) => {
      // Use the map for name lookups
      const aoInfo = allAccountingOfficersMap.get(d.User_ID);
      const altAoInfo = allAccountingOfficersMap.get(d.Alternate_User_ID);

      let accountingOfficerName = aoInfo?.label || `User ${d.User_ID}`;
      let altOfficerName = d.Alternate_User_ID
        ? altAoInfo?.label || `User ${d.Alternate_User_ID}`
        : "N/A";

      // Determine delegation type
      let delegationType = "CG";
      if (d.LG_Vote_ID && Number(d.LG_Vote_ID) > 0) {
        delegationType = "LG";
      } else if (d.SEPC_Vote_ID && Number(d.SEPC_Vote_ID) > 0) {
        delegationType = "SEPC";
      } else if (d.Vote_ID && Number(
        typeof d.Vote_ID === "object" ? d.Vote_ID?.Vote_ID : d.Vote_ID
      ) > 0) {
        delegationType = "CG";
      }

      // Determine if user can edit
      let canEdit = false;

      if (isSuperAdmin) {
        canEdit = !["Approved", "Rejected"].includes(d.Status);
      } else if (isBPEDMaster) {
        const lgVoteId = Number(d.LG_Vote_ID);
        const cgVoteId = Number(
          typeof d.Vote_ID === "object" ? d.Vote_ID?.Vote_ID : d.Vote_ID,
        );
        const isSameType = isLG ? lgVoteId > 0 : cgVoteId > 0;
        canEdit = isSameType && !["Approved", "Rejected"].includes(d.Status);
      }

      return {
        ...d,
        id: d.Report_Delegation_ID,
        start: formatDate(d.Start_Date),
        end: formatDate(d.End_Date),
        canEdit,
        Accounting_Officer_Name: accountingOfficerName,
        Alt_Accounting_Officer_Name: altOfficerName,
        Delegation_Type: delegationType,
      };
    });
  }, [
    filteredDelegations,
    allAccountingOfficersMap,
    isLG,
    isBPEDMaster,
    isSuperAdmin,
  ]);

  // ==================== PERMISSIONS ====================
  const hasActiveDelegation = useMemo(() => {
    if (!data?.Delegations || !user?.User_ID) return false;

    return data.Delegations.some((d) => {
      const isActive = d.Status === "Active";
      const isOwner = d.User_ID === user.User_ID;
      return isActive && isOwner;
    });
  }, [data, user?.User_ID]);

  const canCreate = useMemo(() => {
    if (isSuperAdmin) {
      return !!(voteId && altAccountingOfficerOptions.length > 0);
    }

    if (isBPEDMaster || isAdmin) {
      return false; // BPED Masters and Admins cannot create delegations
    }

    if (isAccountingOfficer) {
      if (hasActiveDelegation) {
        return false;
      }
      return !!(voteId && altAccountingOfficerOptions.length > 0);
    }

    return false;
  }, [
    voteId,
    isBPEDMaster,
    isAdmin,
    isAccountingOfficer,
    isSuperAdmin,
    altAccountingOfficerOptions,
    hasActiveDelegation,
  ]);

  // ==================== ACTION HANDLERS ====================
  const handleAdd = () => {
    if (isSubmitting || createLoading) {
      toastr.warning("Processing", "Please wait, previous request is still processing");
      return;
    }

    if (!canCreate) {
      if (hasActiveDelegation) {
        toastr.warning(
          "Active Delegation Exists",
          "You can only delegate to one user at a time",
        );
      } else if (isBPEDMaster || isAdmin) {
        toastr.warning(
          "Permission Denied",
          "Only Accounting Officers can create delegations.",
        );
      } else if (!voteId) {
        toastr.warning(
          "No Vote Assigned",
          "You need to be assigned to a vote first",
        );
      } else if (!isAccountingOfficer && !isSuperAdmin) {
        toastr.warning(
          "Permission Denied",
          "Only Accounting Officers can create delegations",
        );
      } else if (altAccountingOfficerOptions.length === 0) {
        toastr.warning(
          "No Available Officers",
          "No other accounting officers available in your vote to delegate to",
        );
      }
      return;
    }

    setModalMode("Add");
    setSelectedRow(null);
    setModalOpen(true);
  };

  const handleEdit = (row) => {
    if (isSubmitting || updateLoading) {
      toastr.warning("Processing", "Please wait, previous request is still processing");
      return;
    }

    let canEdit = false;

    if (isSuperAdmin) {
      canEdit = !["Approved", "Rejected"].includes(row.Status);
    } else if (isBPEDMaster) {
      const lgVoteId = Number(row.LG_Vote_ID);
      const cgVoteId = Number(
        typeof row.Vote_ID === "object" ? row.Vote_ID?.Vote_ID : row.Vote_ID,
      );
      const isSameType = isLG ? lgVoteId > 0 : cgVoteId > 0;
      canEdit = isSameType && !["Approved", "Rejected"].includes(row.Status);
    }

    if (!canEdit) {
      toastr.warning("Permission", "Cannot edit this record");
      return;
    }

    setModalMode("Update");
    setSelectedRow(row);
    setModalOpen(true);
  };

  // ==================== SUBMIT HANDLER ====================
  const handleSubmit = useCallback(
    async (formData) => {
      if (isSubmitting) {
        toastr.warning("Processing", "Please wait, your request is being processed");
        return;
      }

      setIsSubmitting(true);

      try {
        if (modalMode === "Add") {
          // Validation
          if (!formData.Comments || formData.Comments.trim() === "") {
            toastr.error("Validation", "Reason for delegation is required");
            setIsSubmitting(false);
            return;
          }

          if (!formData.Start_Date) {
            toastr.error("Validation", "Start date is required");
            setIsSubmitting(false);
            return;
          }

          if (!formData.End_Date) {
            toastr.error("Validation", "End date is required");
            setIsSubmitting(false);
            return;
          }

          if (new Date(formData.Start_Date) > new Date(formData.End_Date)) {
            toastr.error("Validation", "End date cannot be before start date");
            setIsSubmitting(false);
            return;
          }

          if (!formData.Alternate_User_ID) {
            toastr.error(
              "Validation",
              "Please select an alternative accounting officer",
            );
            setIsSubmitting(false);
            return;
          }

          if (formData.Has_Consented !== "1") {
            toastr.error("Validation", "You must consent to the delegation");
            setIsSubmitting(false);
            return;
          }

          if (formData.User_ID === formData.Alternate_User_ID) {
            toastr.error("Validation", "Cannot delegate to yourself");
            setIsSubmitting(false);
            return;
          }

          const variables = {
            ...formData,
            Vote_ID: !isLG ? voteId : 0,
            LG_Vote_ID: isLG ? voteId : 0,
            SEPC_Vote_ID: 0,
          };
          await createDelegation({ variables });
        } else if (modalMode === "Update") {
          const delegationId =
            selectedRow?.Report_Delegation_ID ||
            selectedRow?.Delegation_ID ||
            selectedRow?.id;

          if (!delegationId) {
            toastr.error("Error", "Delegation ID is missing");
            setIsSubmitting(false);
            return;
          }

          if (!formData.Status) {
            toastr.error("Validation", "Status is required");
            setIsSubmitting(false);
            return;
          }

          const variables = {
            id: parseFloat(delegationId),
            data: {
              Status: formData.Status,
              Modified_By: formData.Modified_By || userName || "System",
            },
          };
          await updateDelegation({ variables });
        }
      } catch (error) {
        console.error("Submit error:", error);
        toastr.error("Error", error.message || "Failed to submit delegation");
        setIsSubmitting(false);
      }
    },
    [
      modalMode,
      selectedRow,
      createDelegation,
      updateDelegation,
      userName,
      isLG,
      voteId,
      isSubmitting,
    ],
  );

  // ==================== GRID COLUMNS ====================
  const columns = [
    {
      field: "Delegation_Type",
      headerName: "Type",
      width: 80,
      cellRenderer: (params) => {
        const type = params.value;
        const colors = {
          LG: { bg: "#d4edda", color: "#155724" },
          CG: { bg: "#fff3cd", color: "#856404" },
          SEPC: { bg: "#d1ecf1", color: "#0c5460" },
        };
        const config = colors[type] || colors.CG;
        const span = document.createElement("span");
        span.textContent = type;
        span.style.padding = "2px 8px";
        span.style.borderRadius = "12px";
        span.style.fontSize = "11px";
        span.style.fontWeight = "bold";
        span.style.backgroundColor = config.bg;
        span.style.color = config.color;
        return span;
      },
    },
    {
      field: "Accounting_Officer_Name",
      headerName: "Accounting Officer",
      width: 200,
    },
    {
      field: "Alt_Accounting_Officer_Name",
      headerName: "Alternative Officer",
      width: 200,
    },
    { field: "Comments", headerName: "Reason", flex: 1, minWidth: 250 },
    { field: "start", headerName: "Start Date", width: 120 },
    { field: "end", headerName: "End Date", width: 120 },
    {
      field: "Status",
      headerName: "Status",
      width: 120,
      cellRenderer: (params) => {
        const status = params.value;
        const colors = {
          Active: { bg: "#d1ecf1", color: "#0c5460" },
          Pending: { bg: "#fff3cd", color: "#856404" },
          Approved: { bg: "#d4edda", color: "#155724" },
          Rejected: { bg: "#f8d7da", color: "#721c24" },
        };
        const config = colors[status] || colors.Pending;

        const span = document.createElement("span");
        span.textContent = status;
        span.style.padding = "4px 8px";
        span.style.fontWeight = "500";
        span.style.color = config.color;
        return span;
      },
    },
    {
      headerName: "Action",
      width: 150,
      cellRenderer: (params) => {
        const row = params.data;
        const container = document.createElement("div");
        container.style.display = "flex";
        container.style.gap = "8px";

        if (row.canEdit && !isSubmitting && !updateLoading) {
          const editBtn = document.createElement("button");
          editBtn.textContent = "Edit";
          editBtn.className = "btn btn-sm btn-outline-primary";
          editBtn.style.cursor = "pointer";
          editBtn.style.padding = "4px 8px";
          editBtn.onclick = (e) => {
            e.stopPropagation();
            handleEdit(row);
          };
          container.appendChild(editBtn);
        }

        return container;
      },
    },
  ];

  const isLoading = loading || assignmentsLoading;
  const isCreateDisabled = !canCreate || isSubmitting || createLoading || updateLoading;

  if (isLoading && !data) {
    return (
      <div
        className="d-flex justify-content-center align-items-center"
        style={{ height: "60vh" }}
      >
        <div className="spinner-border text-primary" role="status">
          <span className="sr-only">Loading...</span>
        </div>
      </div>
    );
  }

  const getUserTypeDisplay = () => {
    if (isSuperAdmin) return "All Types";
    const typeLabels = {
      LG: "Local Government",
      CG: "Central Government",
      SEPC: "State Enterprises and Public Corporations",
      SuperAdmin: "Super Administrator",
    };
    return typeLabels[detectedUserType] || detectedUserType;
  };

  return (
    <Card>
      <CardHeader title="Role Delegation">
        <div className="d-flex align-items-center gap-2">
          <TableCardHeader
            handleHButtonClick={handleAdd}
            HButtonText="Create Delegation"
            showAddButton={canCreate}
            disabled={isCreateDisabled}
            tooltip={
              !canCreate
                ? hasActiveDelegation
                  ? "You already have an active delegation"
                  : "Only Accounting Officers can create delegations"
                : ""
            }
          />
        </div>
      </CardHeader>

      <CardBody>
        <AgGridTable
          rowData={rowData}
          gridOptions={{
            columnDefs: columns,
            defaultColDef: { sortable: true, resizable: true, filter: true },
            pagination: true,
            paginationPageSize: 20,
            getRowId: (params) => params.data.id,
            domLayout: "autoHeight",
          }}
          loading={isLoading || isSubmitting}
        />

        {rowData.length === 0 && !isLoading && (
          <div className="text-center py-5">
            <Alert variant="info">
              <Alert.Heading>No delegations found</Alert.Heading>
              <p>
                {isSuperAdmin && "No delegations found in the system."}
                {isBPEDMaster &&
                  `No ${getUserTypeDisplay()} delegations found in the system.`}
                {isAccountingOfficer &&
                  canCreate &&
                  "Click 'Create Delegation' to create your first delegation."}
                {isAccountingOfficer &&
                  !canCreate &&
                  hasActiveDelegation &&
                  "You already have an active delegation. You can only delegate to one user at a time."}
                {isAccountingOfficer &&
                  !canCreate &&
                  !hasActiveDelegation &&
                  "No other accounting officers available in your vote to delegate to."}
                {!isSuperAdmin &&
                  !isBPEDMaster &&
                  !isAccountingOfficer &&
                  "You don't have permission to view delegations."}
              </p>
            </Alert>
          </div>
        )}
      </CardBody>

      <ReportDelegationModal
        show={modalOpen}
        onHide={() => {
          setModalOpen(false);
          setIsSubmitting(false);
        }}
        action={modalMode}
        delegationData={selectedRow}
        handleSubmit={handleSubmit}
        loading={isSubmitting || createLoading || updateLoading}
        Year_ID={activeFiscalYear?.Year_ID || new Date().getFullYear()}
        Vote_ID={!isLG ? voteId : 0}
        LG_Vote_ID={isLG ? voteId : 0}
        SEPC_Vote_ID={isSEPC ? voteId : 0}
        user={user}
        userName={userName}
        accountingOfficerOptions={accountingOfficerOptions}
        altAccountingOfficerOptions={altAccountingOfficerOptions}
        StatusOptions={statusOptions}
        isLG={isLG}
        isCG={isCG}
        isSEPC={isSEPC}
        isSuperAdmin={isSuperAdmin}
        isBPEDMaster={isBPEDMaster}
        isAccountingOfficer={isAccountingOfficer}
        userRoleDisplay={roleTitle}
        voteDisplayInfo={voteDisplayInfo}
        detectedUserType={detectedUserType}
      />
    </Card>
  );
}

export default React.memo(ReportDelegation);